import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import {
  AiChSuggestionMessage,
  AiChattingService,
  FeedbackItem,
  SuggestionDto,
  SuggestionFeedbackStatus,
} from '@api'
import { useUnit } from 'effector-react'

import { useToast } from '@/components/ui/toast'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { clearChatSuggestionsByVersion } from '@/models/chattingUI/chatSuggestions/chatSuggestions.ts'
import {
  clearSuggestionSendingState,
  startSendingMessages,
  updateSendingProgress,
} from '@/models/chattingUI/chatSuggestions/messageSendingTracker.ts'
import { $activeChatDetails, $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  startChatterTyping,
  stopChatterTyping,
} from '@/models/chattingUI/chats/typing.ts'
import { sendMessageFx } from '@/models/chattingUI/messages/messages.ts'
import { startTypingToFanFx } from '@/models/chattingUI/pusher.ts'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { MessageSendRequest } from '@/modules/chatting/types.ts'
import { handleError } from '@/utils/error'
import { delayMessageSend } from '@/utils/message-delay'

import { ChatBlockAction } from './ChatBlockAction'
import { ChatSuggestionMessage } from './ChatSuggestionMessage'
import { ChatSuggestionsHeader } from './ChatSuggestionsHeader'
import { ChatSuggestionsHeaderActions } from './ChatSuggestionsHeaderActions'

interface Props {
  suggestions: AiChSuggestionMessage
}

export const ChatSuggestionsBlock: FC<Props> = ({ suggestions }) => {
  const [scoring, setScoring] = useState<Record<number, number>>({})
  const [feedback, setFeedback] = useState<Record<number, string>>({})
  const [prices, setPrices] = useState<Record<number, Record<number, number>>>(
    {},
  )
  const [step, setStep] = useState<'view' | 'scoring' | 'feedback'>('view')
  const [isProcessing, setIsProcessing] = useState(false)

  const fanId = useUnit($fanId)
  const activeCreator = useUnit($currentCreator)
  const activeChat = useUnit($activeChatDetails)
  const creatorId = useUnit($creatorId)
  const toast = useToast()

  useEffect(() => {
    setScoring({})
    setFeedback({})
    setPrices({})
    setStep('view')
    setIsProcessing(false)
  }, [suggestions])

  const handleScoreSelect = (index: number, score: number) => {
    setScoring((prev) => ({ ...prev, [index]: score }))
  }

  const handleFeedbackChange = (index: number, value: string) => {
    setFeedback((prev) => ({ ...prev, [index]: value }))
  }

  const handlePricesChange = useCallback(
    (index: number, value: Record<number, number>) => {
      setPrices((prev) => ({ ...prev, [index]: value }))
    },
    [],
  )

  const rateMessages = useCallback(
    async (defaultRate = 10, acceptedIndex?: number) => {
      if (
        !suggestions.suggestions ||
        !creatorId ||
        !suggestions.ofCreatorId ||
        !suggestions.ofFanId
      ) {
        toast.error('Missing required data for rating messages')

        return
      }

      if (isProcessing) {
        return
      }

      setIsProcessing(true)

      const decisionData: FeedbackItem[] = suggestions.suggestions.map(
        (_, index) => ({
          status:
            acceptedIndex === undefined
              ? SuggestionFeedbackStatus.REJECTED
              : acceptedIndex === index
                ? SuggestionFeedbackStatus.ACCEPTED
                : SuggestionFeedbackStatus.REJECTED,
          score: scoring[index] || defaultRate,
          feedback: feedback[index] || '',
          aiOutput: _.messages?.map((message) => message.text).join(' ') || '',
        }),
      )

      try {
        await AiChattingService.markSuggestionAccepted({
          creatorId,
          ofCreatorId: Number(suggestions.ofCreatorId),
          ofFanId: Number(suggestions.ofFanId),
          requestBody: {
            version: suggestions.version!,
            ofFanId: Number(suggestions.ofFanId),
            ofCreatorId: Number(suggestions.ofCreatorId),
            occurredAt: new Date().toISOString().replace('Z', ''),
            decisionData,
          },
        })

        clearChatSuggestionsByVersion({
          fanId: Number(suggestions.ofFanId),
          version: suggestions.version || 0,
        })
      } catch (error) {
        handleError(error, 'Failed to rate messages. Please try again.')
        if (fanId && activeCreator?.onlyfansId) {
          await AiChattingService.getAiChSuggestions({
            creatorId,
            ofFanId: fanId,
            ofCreatorId: activeCreator.onlyfansId,
          })
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [
      suggestions.suggestions,
      suggestions.ofCreatorId,
      suggestions.ofFanId,
      suggestions.version,
      creatorId,
      isProcessing,
      toast,
      scoring,
      feedback,
      fanId,
      activeCreator?.onlyfansId,
    ],
  )

  const handleSend = useCallback(
    async (message: SuggestionDto, index: number) => {
      if (
        !message?.messages?.length ||
        !activeChat?.withUser?.id ||
        isProcessing ||
        !fanId ||
        suggestions.version === undefined ||
        suggestions.version === null
      ) {
        return
      }

      const messagePrice = prices[index]
      const totalMessages = message.messages.length

      startSendingMessages({
        fanId,
        version: suggestions.version ?? 0,
        suggestionIndex: index,
        totalMessages,
      })

      setIsProcessing(true)

      try {
        for (const [messageIndex, mess] of message.messages.entries()) {
          const price =
            messagePrice?.[messageIndex] !== undefined
              ? messagePrice[messageIndex]
              : mess.price

          if (messageIndex > 0) {
            if (mess.text) {
              startChatterTyping(activeChat.withUser.id)
            }
            await delayMessageSend(mess.text)
            stopChatterTyping(activeChat.withUser.id)
          }

          await sendMessageFx({
            userId: activeChat.withUser.id,
            message: {
              ...mess,
              media: mess.mediaFiles,
              price,
            } as Partial<MessageSendRequest>,
            chat: activeChat,
            optimisticMessageId: crypto.randomUUID
              ? (crypto.randomUUID() as unknown as number)
              : Date.now() + Math.floor(Math.random() * 1000000),
          })

          updateSendingProgress({
            fanId,
            version: suggestions.version ?? 0,
            suggestionIndex: index,
            sentMessages: messageIndex + 1,
          })
        }

        await rateMessages(undefined, index)

        clearSuggestionSendingState({
          fanId,
          version: suggestions.version ?? 0,
          suggestionIndex: index,
        })

        toast.success('Messages sent successfully')
      } catch (error) {
        clearSuggestionSendingState({
          fanId,
          version: suggestions.version ?? 0,
          suggestionIndex: index,
        })

        handleError(error, 'Failed to send messages. Please try again.')
      } finally {
        setIsProcessing(false)
      }
    },
    [
      activeChat,
      prices,
      isProcessing,
      rateMessages,
      toast,
      fanId,
      suggestions.version,
    ],
  )

  const handleChangeStep = (step: 'view' | 'scoring' | 'feedback') => {
    startTypingToFanFx(fanId)

    setStep(step)
  }

  const submitDisabledReason = useMemo(() => {
    if (step === 'scoring') {
      const scores = Object.values(scoring)

      if (scores.length !== suggestions.suggestions?.length) {
        return 'Please score all messages'
      }
    }

    return ''
  }, [scoring, step, suggestions.suggestions?.length])

  const handleRegenerate = useCallback(() => {
    if (isProcessing) {
      return
    }

    setScoring({})
    setStep('feedback')
  }, [isProcessing])

  const handleSendRegenerate = useCallback(async () => {
    if (isProcessing) {
      return
    }

    setScoring({})
    await rateMessages(1)
    setStep('view')
  }, [rateMessages, isProcessing])

  const handleBack = useCallback(() => {
    if (isProcessing) {
      return
    }

    setStep('view')
    setScoring({})
    setFeedback({})
  }, [isProcessing])

  const handleOpenMediaPreview = useCallback(
    (index: number) => {
      const suggestion = suggestions.suggestions?.[index]

      if (!suggestion || isProcessing) {
        return
      }

      openDialog({
        type: DIALOG_TYPE.CHAT_SUGGESTIONS,
        suggestion: suggestion,
        handlePricesChange: handlePricesChange,
        messageIndex: index,
        currentPrice: prices[index],
      })
    },
    [suggestions.suggestions, handlePricesChange, prices, isProcessing],
  )

  return (
    <div className="pt-5 bottom-full left-0 w-full z-20 -translate-y-2">
      <div className="relative rounded-lg p-[1px] bg-gradient-to-r from-[#FF6DEC] via-[#C76DFF] via-[#836DFF] via-[#6DC0FF] via-[#6DFFF5] to-[#6DFFB9] animate-gradient-rotate">
        <div className="bg-card px-6 py-4 rounded-lg w-full overflow-hidden gap-4 flex flex-col">
          <ChatSuggestionsHeader
            step={step}
            onBack={handleBack}
          >
            <ChatSuggestionsHeaderActions onRegenerate={handleRegenerate} />
          </ChatSuggestionsHeader>
          <div className="flex gap-3">
            <div
              className="flex flex-col gap-3 w-full overflow-hidden"
              id="chat-message-suggestions-block"
            >
              {suggestions.suggestions?.map((suggestion, index) => (
                <ChatSuggestionMessage
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  message={suggestion}
                  messageNumber={index}
                  onScoreSelect={handleScoreSelect}
                  onFeedbackChange={handleFeedbackChange}
                  selectedScore={scoring}
                  step={step}
                  onChangeStep={handleChangeStep}
                  onSend={handleSend}
                  submitDisabledReason={submitDisabledReason}
                  onOpenMediaPreview={() => handleOpenMediaPreview(index)}
                />
              ))}
            </div>
            {step === 'feedback' && (
              <ChatBlockAction onSubmit={handleSendRegenerate} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
